import { EPaginationParams } from 'features/Pagination';
import { commonApi } from 'shared/api/commonApi';
import { EApiTags } from 'shared/typings';
import { getCookie } from 'shared/utils/getCookie';

import { ISetReadRequest, IUnreadCount, TNotificationsList } from '../model/types';

export const notificationApi = commonApi
	.injectEndpoints({
		endpoints: (builder) => ({
			getNotificationsList: builder.query<TNotificationsList, string>({
				query: (params) => ({
					url: `notifications/${params ? '?' : ''}${params}`,
				}),
				serializeQueryArgs: ({ endpointName }) => {
					return endpointName;
				},
				merge: (cache, response, otherArgs) => {
					const offset = new URLSearchParams(otherArgs.arg).get(EPaginationParams.OFFSET);

					return !offset
						? response
						: {
								...response,
								results: [...(cache.results || []), ...(response.results || [])],
							};
				},
				forceRefetch({ currentArg, previousArg }) {
					return currentArg !== previousArg;
				},
			}),

			getNotificationsPopup: builder.query<TNotificationsList, void>({
				query: () => ({
					url: `notifications/?limit=5`,
				}),
			}),

			getNotificationsCount: builder.query<IUnreadCount, void>({
				query: () => ({
					url: 'notifications/unread_count/',
				}),
			}),

			setRead: builder.mutation<ISetReadRequest, Partial<ISetReadRequest>>({
				query(body) {
					return {
						url: 'notifications/mark_as_read/',
						headers: { 'X-Csrftoken': getCookie('csrftoken') },
						method: 'POST',
						body,
					};
				},
			}),

			setRejected: builder.mutation<ISetReadRequest, Partial<ISetReadRequest>>({
				query(body) {
					return {
						url: 'notifications/mark_as_rejected/',
						headers: { 'X-Csrftoken': getCookie('csrftoken') },
						method: 'POST',
						body,
					};
				},
			}),
		}),
		overrideExisting: false,
	})
	.enhanceEndpoints({
		addTagTypes: [EApiTags.NOTIFICATIONS_COUNT, EApiTags.NOTIFICATIONS_LIST],
		endpoints: {
			getNotificationsList: {
				providesTags: [EApiTags.NOTIFICATIONS_LIST],
			},
			getNotificationsPopup: {
				providesTags: [EApiTags.NOTIFICATIONS_LIST],
				keepUnusedDataFor: 0,
			},
			getNotificationsCount: {
				providesTags: [EApiTags.NOTIFICATIONS_COUNT],
			},
			setRead: {
				invalidatesTags: [EApiTags.NOTIFICATIONS_COUNT],
			},
			setRejected: {
				invalidatesTags: [EApiTags.NOTIFICATIONS_LIST],
			},
		},
	});

export const {
	useGetNotificationsListQuery,
	useGetNotificationsPopupQuery,
	useSetReadMutation,
	useSetRejectedMutation,
	useGetNotificationsCountQuery,
} = notificationApi;
